import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { typedKeptMessage } from "@/lib/typedAmountKept";   // Step 12 add-on G6.1: one helper for Cancel
import { asArray } from "@/lib/asArray";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API_URL } from "@/lib/api";
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import RiskBadge from "@/components/RiskBadge";
import AILabel from "@/components/AILabel";
import { Play, Download, Sparkles, ExternalLink, Paperclip, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import AttachmentPanel from "@/components/AttachmentPanel";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

export default function Discrepancies() {
  const colW = useResizableColumns({ storageKey: "discrepancies", defaults: [240, 110, 240, 110, 160, 130], mins: [130, 72, 130, 72, 100, 90] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [risk, setRisk] = useState("__all__");
  const [loading, setLoading] = useState(false);
  const [explaining, setExplaining] = useState({});
  const [attachDisc, setAttachDisc] = useState(null);
  // Batch A3 — per-finding resolution edits (Reason not available,
  // Alternative procedure, Result) buffered here and flushed on blur.
  const [resDraft, setResDraft] = useState({});   // { [disc_id]: {reason_not_available, alternative_procedure, result, difference_amount, difference_type} }
  const [sending, setSending] = useState({});    // { [disc_id]: bool }
  const [sentIds, setSentIds] = useState({});    // { [disc_id]: misstatement.id }
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link: scroll to & flash the highlighted discrepancy row when linked from global search.
  useEffect(() => {
    const hl = searchParams.get("highlight");
    if (!hl || items.length === 0) return;
    const el = document.querySelector(`[data-testid="disc-row-${hl}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-emerald-500");
    const t = setTimeout(() => el.classList.remove("ring-2", "ring-emerald-500"), 2400);
    // Clean URL so refresh doesn't re-flash.
    const next = new URLSearchParams(searchParams); next.delete("highlight");
    setSearchParams(next, { replace: true });
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [items, searchParams.toString()]);

  const engId = current?.id;
  const load = useCallback(async () => {
    if (!engId) return;
    const url = risk && risk !== "__all__" ? `/discrepancies/${engId}?risk=${risk}` : `/discrepancies/${engId}`;
    const r = await api.get(url);
    setItems(r.data);
    // Seed which findings already have a misstatement sent.
    const nextSent = {};
    asArray(r.data).forEach((d) => {
      if (d.misstatement_id) nextSent[d.id] = d.misstatement_id;
    });
    setSentIds(nextSent);
  }, [engId, risk]);

  // Batch A3 helpers.
  const NF = { format: (n) => fmtNumber(n) };   // STEP 3 · rule 2 · B-2e
  const draftOf = (d) => resDraft[d.id] ?? {
    reason_not_available: d.reason_not_available || "",
    alternative_procedure: d.alternative_procedure || "",
    result: d.result || "",
    difference_amount: d.difference_amount ?? "",
    difference_type: d.difference_type || "Factual",
  };
  const setDraft = (id, patch) => setResDraft((s) => ({ ...s, [id]: { ...draftOf({ id, ...s[id] }), ...patch } }));
  // STEP 11d · A9: resolves true when saved (or nothing changed), false when the save was refused (the error is shown)
  const flushResolution = async (d, patch) => {
    try {
      const body = { ...draftOf(d), ...patch };
      // Only send changed fields
      const changed = {};
      ["reason_not_available", "alternative_procedure", "result",
       "difference_amount", "difference_type"].forEach((k) => {
        if ((body[k] ?? "") !== (d[k] ?? "")) changed[k] = body[k];
      });
      if (!Object.keys(changed).length) return true;
      if (typeof changed.difference_amount === "string" && changed.difference_amount !== "")
        changed.difference_amount = Number(String(changed.difference_amount).replace(/[^\d.\-]/g, ""));
      await api.patch(`/discrepancies/${d.id}/resolution`, changed);
      setItems((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...changed } : x)));
      return true;
    } catch (e) {
      toast.error(toErrMsgSafe(e));
      return false;
    }
  };
  const toErrMsgSafe = (e) => (typedKeptMessage(e) || e?.response?.data?.detail || e?.message || t("common.failed"));   // Step 12 add-on G6.1
  const sendToMisstatements = async (d) => {
    if (sending[d.id] || sentIds[d.id]) return;
    setSending((s) => ({ ...s, [d.id]: true }));
    // STEP 11d · A9 (find 30): the row's unsaved resolution is saved first; a refused save shows its error and nothing is sent
    if (!(await flushResolution(d, {}))) { setSending((s) => ({ ...s, [d.id]: false })); return; }
    try {
      const payload = {
        source_kind: "discrepancy",
        source_id: d.id,
        fs_head: d.voucher_fs_head || d.fs_head || "1203",
        description: `${d.disc_type}: ${d.description}`.slice(0, 500),
        amount: Number(draftOf(d).difference_amount || 0),
        mtype: draftOf(d).difference_type || "Factual",
        link_back: `/discrepancies?highlight=${d.id}`,
        voucher: d.voucher_no || "",      // Batch 29 · M65: the item key (voucher no.)
      };
      const r = await api.post(`/engagements/${current.id}/misstatements/send-from-finding`, payload);
      setSentIds((s) => ({ ...s, [d.id]: r.data?.id }));
      setItems((prev) => prev.map((x) => (x.id === d.id ? { ...x, misstatement_id: r.data?.id } : x)));
      toast.success(t("disc.a3.sentToast"));
    } catch (e) {      // Batch 29 · M65: already in Misstatements → offer the link
      let done = "";
      try { done = await offerLink(e, { engId: current.id, lang }); } catch (e2) { toast.error(toErrMsgSafe(e2)); done = "error"; }
      if (done === "linked") {
        const mid = e?.response?.data?.existing?.id;
        setSentIds((s) => ({ ...s, [d.id]: mid }));
        setItems((prev) => prev.map((x) => (x.id === d.id ? { ...x, misstatement_id: mid } : x)));
      } else if (!done) toast.error(toErrMsgSafe(e));
    } finally {
      setSending((s) => ({ ...s, [d.id]: false }));
    }
  };
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const r = await api.post(`/discrepancies/${current.id}/run`);
      toast.success(`${r.data.count} findings detected`);
      await load();
    } catch (e) {
      toast.error(t("common.failed"));
    } finally { setLoading(false); }
  };

  const explain = async (id) => {
    setExplaining((s) => ({ ...s, [id]: true }));
    try {
      const r = await api.post(`/discrepancies/${id}/explain`);
      setItems((prev) => prev.map((d) => (d.id === id ? { ...d, ai_explanation: r.data.explanation } : d)));
    } finally { setExplaining((s) => ({ ...s, [id]: false })); }
  };

  const exportXlsx = async () => {
    const token = localStorage.getItem("af_token");
    const resp = await fetch(`${API_URL}/discrepancies/${current.id}/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `discrepancies_${current.id}.xlsx`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPdf = async () => {
    const token = localStorage.getItem("af_token");
    const resp = await fetch(`${API_URL}/pdf/discrepancies/${current.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `discrepancies_${current.engagement_name || current.client_name || current.id}.pdf`; a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  const counts = { High: 0, Medium: 0, Low: 0 };
  items.forEach((i) => {
    // A3 fix — DB may store risk_level as lowercase; normalise.
    const k = (i.risk_level || "").toString();
    const key = k.charAt(0).toUpperCase() + k.slice(1).toLowerCase();
    if (counts[key] !== undefined) counts[key]++;
  });

  return (
    <div className="space-y-6" data-testid="discrepancies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("disc.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("disc.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={run} disabled={loading} className="bg-violet-600 hover:bg-violet-700" data-testid="disc-run">
            <Play className="me-1 h-4 w-4" /> {loading ? t("common.loading") : t("disc.run")}
          </Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="disc-export">
            <Download className="me-1 h-4 w-4" /> XLSX
          </Button>
          <Button variant="outline" onClick={exportPdf} data-testid="disc-export-pdf">
            <Download className="me-1 h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card data-testid="disc-count-high"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.high")}</div>
            <div className="mt-2 text-3xl font-bold text-red-600">{counts.High}</div></div>
          <RiskBadge level="High" />
        </CardContent></Card>
        <Card data-testid="disc-count-med"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.medium")}</div>
            <div className="mt-2 text-3xl font-bold text-amber-600">{counts.Medium}</div></div>
          <RiskBadge level="Medium" />
        </CardContent></Card>
        <Card data-testid="disc-count-low"><CardContent className="p-4 flex items-center justify-between">
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.low")}</div>
            <div className="mt-2 text-3xl font-bold text-emerald-600">{counts.Low}</div></div>
          <RiskBadge level="Low" />
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {t("b3.allFindings")} <AILabel />
          </CardTitle>
          <div className="w-40">
            <Select value={risk} onValueChange={setRisk}>
              <SelectTrigger data-testid="disc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("disc.filterAll")}</SelectItem>
                <SelectItem value="High">{t("common.high")}</SelectItem>
                <SelectItem value="Medium">{t("common.medium")}</SelectItem>
                <SelectItem value="Low">{t("common.low")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ResizableTable cols={colW} wrap className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
              <tr>
                <ResizableTh index={0} className="px-4 py-2 text-start">{t("disc.risk")}</ResizableTh>
                <ResizableTh index={1} className="px-4 py-2 text-start">{t("disc.type")}</ResizableTh>
                <ResizableTh index={2} className="px-4 py-2 text-start">{t("disc.description")}</ResizableTh>
                <ResizableTh index={3} className="px-4 py-2 text-start">{t("disc.voucherRef")}</ResizableTh>
                <ResizableTh index={4} className="px-4 py-2 text-start">{t("disc.docRefs")}</ResizableTh>
                <ResizableTh index={5} className="px-4 py-2 text-end">{t("common.actions")}</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {t("disc.noFindings")}
                </td></tr>
              )}
              {items.map((d) => (
                <tr key={d.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50" data-testid={`disc-row-${d.id}`}>
                  <td className="px-4 py-2"><RiskBadge level={d.risk_level} /></td>
                  <td className="px-4 py-2 font-medium text-xs">
                    {t(`disc.typeLabels.${d.disc_type}`) || d.disc_type}
                  </td>
                  <td className="px-4 py-2">
                    <div>{d.disc_type === "missing_document" ? t("disc.a3.missingSupportText") : ((lang === "ar" && d.description_ar) || d.description)}</div>
                    {d.ai_explanation && (
                      <div className="mt-1 rounded-md border border-violet-200 bg-violet-50 p-2 text-xs text-violet-900 dark:bg-violet-950/30 dark:border-violet-900 dark:text-violet-200">
                        {d.ai_explanation}
                      </div>
                    )}
                    {/* Batch A3 — resolution fields for missing-support findings. */}
                    {d.disc_type === "missing_document" && (() => {
                      const dr = draftOf(d);
                      const canResolve = !!(dr.reason_not_available?.trim()) && !!(dr.alternative_procedure?.trim());
                      const canSend = dr.result === "difference_found";
                      const sentId = sentIds[d.id];
                      return (
                        <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-2 dark:border-slate-800 dark:bg-slate-900/40"
                             data-testid={`disc-a3-block-${d.id}`}>
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            <label className="block">
                              <span className="text-[10px] uppercase text-muted-foreground">{t("disc.a3.reasonNotAvailable")}</span>
                              <textarea rows={2}
                                value={dr.reason_not_available}
                                onChange={(e) => setDraft(d.id, { reason_not_available: e.target.value })}
                                onBlur={() => flushResolution(d, {})}
                                disabled={!!sentId}
                                className="mt-0.5 w-full rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-slate-100 dark:bg-slate-900 dark:disabled:bg-slate-950"
                                data-testid={`disc-a3-reason-${d.id}`} />
                            </label>
                            <label className="block">
                              <span className="text-[10px] uppercase text-muted-foreground">{t("disc.a3.alternativeProcedure")}</span>
                              <textarea rows={2}
                                value={dr.alternative_procedure}
                                onChange={(e) => setDraft(d.id, { alternative_procedure: e.target.value })}
                                onBlur={() => flushResolution(d, {})}
                                disabled={!!sentId}
                                className="mt-0.5 w-full rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-slate-100 dark:bg-slate-900 dark:disabled:bg-slate-950"
                                data-testid={`disc-a3-alt-${d.id}`} />
                            </label>
                          </div>
                          <div className="flex flex-wrap items-end gap-2">
                            <label className="block">
                              <span className="text-[10px] uppercase text-muted-foreground">{t("disc.a3.result")}</span>
                              <select
                                value={dr.result}
                                onChange={(e) => { setDraft(d.id, { result: e.target.value }); flushResolution(d, { result: e.target.value }); }}
                                disabled={!canResolve || !!sentId}
                                className="ms-1 rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-slate-100 dark:bg-slate-900"
                                title={!canResolve ? t("disc.a3.resolveGate") : ""}
                                data-testid={`disc-a3-result-${d.id}`}>
                                <option value="">{t("disc.a3.resultChoose")}</option>
                                <option value="no_difference">{t("disc.a3.resultNoDiff")}</option>
                                <option value="difference_found">{t("disc.a3.resultDiff")}</option>
                              </select>
                            </label>
                            {dr.result === "difference_found" && (
                              <>
                                <label className="block">
                                  <span className="text-[10px] uppercase text-muted-foreground">{t("disc.a3.diffAmount")}</span>
                                  <input type="text" inputMode="decimal"
                                    value={dr.difference_amount === "" ? "" : NF.format(Number(dr.difference_amount || 0))}
                                    onChange={(e) => setDraft(d.id, { difference_amount: e.target.value })}
                                    onBlur={() => flushResolution(d, {})}
                                    disabled={!!sentId}
                                    className="ms-1 w-28 rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-slate-100 dark:bg-slate-900"
                                    data-testid={`disc-a3-diff-amt-${d.id}`} />
                                </label>
                                <label className="block">
                                  <span className="text-[10px] uppercase text-muted-foreground">{t("disc.a3.diffType")}</span>
                                  <select
                                    value={dr.difference_type}
                                    onChange={(e) => { setDraft(d.id, { difference_type: e.target.value }); flushResolution(d, { difference_type: e.target.value }); }}
                                    disabled={!!sentId}
                                    className="ms-1 rounded border px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-slate-100 dark:bg-slate-900"
                                    data-testid={`disc-a3-diff-type-${d.id}`}>
                                    <option value="Factual">{t("disc.a3.mtypeFactual")}</option>
                                    <option value="Judgmental">{t("disc.a3.mtypeJudgmental")}</option>
                                    <option value="Projected">{t("disc.a3.mtypeProjected")}</option>
                                  </select>
                                </label>
                              </>
                            )}
                            {!sentId && (
                              <Button size="sm" variant="outline"
                                      onClick={() => sendToMisstatements(d)}
                                      disabled={!canSend || !!sending[d.id]}
                                      className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                                      title={!canSend ? t("disc.a3.sendGate") : ""}
                                      data-testid={`disc-a3-send-${d.id}`}>
                                <Send className="me-1 h-3 w-3" />
                                {sending[d.id] ? t("common.loading") : t("disc.a3.sendBtn")}
                              </Button>
                            )}
                            {sentId && (
                              <a href={`/completion/misstatements?highlight=${sentId}`}
                                 className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300"
                                 data-testid={`disc-a3-sent-${d.id}`}>
                                <CheckCircle2 className="me-1 h-3 w-3" />
                                {t("disc.a3.sentIndicator")}
                                <ExternalLink className="ms-1 h-3 w-3" />
                              </a>
                            )}
                            {!canResolve && (
                              <span className="text-[10px] text-amber-700" data-testid={`disc-a3-gate-${d.id}`}>
                                {t("disc.a3.resolveGate")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {d.voucher_no ? (
                      <button
                        onClick={() => nav("/vouching")}
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300"
                        data-testid={`disc-voucher-${d.id}`}
                        title={d.voucher_account || ""}
                      >
                        {d.voucher_no} <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {(d.document_refs || []).length === 0 && <span className="text-muted-foreground">—</span>}
                      {asArray(d.document_refs).map((r) => (
                        <button
                          key={r.id}
                          onClick={() => nav("/documents")}
                          className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200"
                          data-testid={`disc-doc-${d.id}-${r.id}`}
                          title={r.filename || ""}
                        >
                          {r.doc_ref || "DOC"} <ExternalLink className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-end">
                    <Button size="sm" variant="ghost" onClick={() => explain(d.id)} disabled={explaining[d.id]} data-testid={`disc-explain-${d.id}`}>
                      <Sparkles className="me-1 h-3 w-3 text-violet-600" />
                      {explaining[d.id] ? "…" : t("disc.explain")}
                    </Button>
                    <Button size="sm" variant="ghost" className="ms-1"
                            onClick={() => setAttachDisc(d)}
                            data-testid={`disc-attach-${d.id}`}>
                      <Paperclip className="me-1 h-3 w-3 text-emerald-600" />
                      {t("disc.attach")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResizableTable>
        </CardContent>
      </Card>

      <Dialog open={!!attachDisc} onOpenChange={(v) => !v && setAttachDisc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("common.supportingDocs")} — {attachDisc?.disc_type?.replace(/_/g, " ")}</DialogTitle>
          </DialogHeader>
          {attachDisc && (
            <AttachmentPanel engagementId={current.id}
                              contextType="discrepancy" contextId={attachDisc.id}
                              locked={!!current.signed_off}
                              label={t("disc.attachLabel")} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
